import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { FuelService } from 'src/app/services/fuel/fuel.service';
import { FuelModalService } from 'src/app/services/fuel-modal/fuel-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-fuel',
  templateUrl: './fuel.component.html',
  styleUrls: ['./fuel.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class FuelComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  fuels: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedFuels: string[] = [];

  constructor(
    private _fuelService: FuelService,
    private _fuelModalService: FuelModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadFuels();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadFuels(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedFuels = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._fuelService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.fuels = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.fuels = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading fuels:', error);
          this.fuels = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadFuels(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadFuels(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadFuels();
  }

  openAddFuelModal(): void {
    this._fuelModalService.clearSelectedFuel();
    const modalElement = document.getElementById('kt_modal_fuel_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(fuel: any): void {
    this._fuelModalService.setSelectedFuel(fuel);
    const modalElement = document.getElementById('kt_modal_fuel_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteFuel(fuel: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${fuel.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(fuel);
      }
    });
  }

  private performDelete(fuel: any): void {
    const fuelId = fuel._id || fuel.id;
    this.subscriptions.push(
      this._fuelService.DeleteDataById(fuelId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Fuel has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadFuels(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete fuel.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting fuel.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onFuelAdded(): void {
    console.log('Fuel added, refreshing list...');
    this.loadFuels(this.currentPage, this.searchTerm, this.filters);
  }

  onFuelUpdated(): void {
    console.log('Fuel updated, refreshing list...');
    this.loadFuels(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(fuelId: string): boolean {
    return this.selectedFuels.includes(fuelId);
  }

  isAllSelected(): boolean {
    return this.fuels.length > 0 && this.selectedFuels.length === this.fuels.length;
  }

  isIndeterminate(): boolean {
    return this.selectedFuels.length > 0 && this.selectedFuels.length < this.fuels.length;
  }

  toggleSelection(fuelId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedFuels.includes(fuelId)) {
        this.selectedFuels.push(fuelId);
      }
    } else {
      this.selectedFuels = this.selectedFuels.filter(id => id !== fuelId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedFuels = this.fuels.map(fuel => fuel._id);
    } else {
      this.selectedFuels = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-fuel-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-fuel-table-toolbar="selected"]');

    if (this.selectedFuels.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedFuels(): void {
    if (this.selectedFuels.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedFuels.length} selected fuel(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedFuels.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._fuelService.BulkDelete(this.selectedFuels).subscribe({
        next: () => {
          this.loadFuels();
          this.selectedFuels = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected fuels have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting fuels:', error);
          Swal.fire('Error!', 'Failed to delete selected fuels.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
